/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step3-columns.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Tab, Tabs } from '@mui/material';
import { useTranslation } from 'hooks';
import {
    ReportDefinitionFields,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab_columns_ReportDefinitionColumn as ReportDefinitionFields_tabs_columns,
} from 'queries/generated/fragments.generated';
import { ReportDefinitionTabs } from 'types/graphql-global-types';
import MultiListDragDrop, { DragDropListItem } from 'views/components/core/multi-list-drag-drop';
import { getTab } from 'views/components/reports/custom-report-config/custom-report-helpers';
import { textToId } from '@utils/automation-test-helpers';
import { AddEditCustomReportState } from './index';

interface CustomReportStep3ColumnsProps {
    show: boolean;
    optionsTabs: ReportDefinitionFields_tabs[];
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    resetColumns: (tabType: ReportDefinitionTabs) => void;
}

const CustomReportStep3Columns = ({
    show,
    optionsTabs,
    reportDefinition,
    state,
    setState,
    resetColumns,
}: CustomReportStep3ColumnsProps) => {
    const { t } = useTranslation();
    const [tabNumber, setTabNumber] = useState(0);

    const getCurrentTabType = () => reportDefinition.tabs[tabNumber]?.tabType;

    const getListItems = (inputColumns: ReportDefinitionFields_tabs_columns[]) => {
        if (!inputColumns) return [] as DragDropListItem[];

        return inputColumns.map(
            (col) =>
                ({
                    key: col.dataField,
                    text: t(`reports:${col.dataField}`),
                }) as DragDropListItem,
        );
    };

    const getColumns = (listItems: DragDropListItem[]) =>
        listItems.map(
            (listItem) =>
                ({
                    __typename: 'ReportDefinitionColumn',
                    dataField: listItem.key,
                    index: 0,
                }) as ReportDefinitionFields_tabs_columns,
        );

    const getNotSelectedColumns = () => {
        const optionsTab = getTab(optionsTabs, getCurrentTabType());

        if (!optionsTab) return [] as ReportDefinitionFields_tabs_columns[];

        return optionsTab.columns;
    };

    const getUpdatedReportDefinitionTabs = (
        tabsToUpdate: ReportDefinitionFields_tabs[],
        columns: ReportDefinitionFields_tabs_columns[],
    ) => {
        const updatedTabs = [...tabsToUpdate];

        getTab(tabsToUpdate, getCurrentTabType()).columns = [...columns];

        return updatedTabs;
    };

    const update = (list1Items: DragDropListItem[], list2Items: DragDropListItem[]) => {
        const updatedReportDefinitionTabs = getUpdatedReportDefinitionTabs(
            reportDefinition.tabs,
            getColumns(list2Items),
        );
        const updatedOptionsTabs = getUpdatedReportDefinitionTabs(optionsTabs, getColumns(list1Items));

        setState({
            ...state,
            reportDefinition: {
                ...state.reportDefinition,
                tabs: updatedReportDefinitionTabs,
            },
            optionsTabs: updatedOptionsTabs,
        });
    };

    const reset = () => {
        resetColumns(getCurrentTabType());
    };

    const getTabs = () =>
        reportDefinition.tabs.map((tab: ReportDefinitionFields_tabs, index) => (
            <Tab
                key={tab.tabType}
                label={t(`reports:${tab.tabType}`)}
                className="custom-report-column-selection-tab"
                data-testid={`tab-${textToId(tab.tabType, index)}`}
            />
        ));

    const onChangeTab = (e: ChangeEvent<{}>, newValue: number) => {
        setTabNumber(newValue);
    };

    useEffect(() => {
        setTabNumber(0);
    }, [show]);

    return (
        <>
            {show && (
                <div className="step3-columns">
                    <Tabs
                        value={tabNumber}
                        onChange={onChangeTab}
                        indicatorColor="primary"
                        textColor="primary"
                        centered
                        variant="scrollable"
                    >
                        {getTabs()}
                    </Tabs>
                    <MultiListDragDrop
                        updateParent={update}
                        list1Items={getListItems(getNotSelectedColumns())}
                        list2Items={getListItems(reportDefinition.tabs[tabNumber]?.columns)}
                        list1Header={t('reports:customReportStep3ColumnsNotSelected')}
                        list2Header={t('reports:customReportStep3ColumnsSelected')}
                        reset={reset}
                    />
                </div>
            )}
        </>
    );
};

export default CustomReportStep3Columns;
